/**
 * What a shopper is told about a limited drop-table entry, in one place so the box page, the
 * dashboard's preview of it and the inventory page all say the same thing.
 *
 * The rules being disclosed, and where each one is enforced:
 *
 *  - A limited entry has a counted supply (`isLimited`, `limitedQuantity`), so another shopper
 *    can withdraw the last unit first.
 *  - A withdrawal of an item nobody can supply is refused with 409
 *    `inventory_item_not_withdrawable`. The shopper keeps the item and sells it for its value
 *    through `POST /v1/me/inventory/{id}/sell`. Nothing is credited on its own.
 *  - A MARKETPLACE entry is bought from an outside seller at withdrawal. Every withdrawal pays
 *    the environment's destination cost; a MARKETPLACE line adds the seller's surcharge above
 *    its US price on top, and some sellers have no price for a destination at all (422
 *    `unsupported_shipping_destination`).
 *  - A cancelled marketplace order writes the shopper a voucher worth the item's value
 *    (`settleCancelledWithdrawalLines`). That is the one place the word voucher belongs.
 *
 * `docs/fulfillment/tenants.md` holds the same rules for a tenant reader.
 */
import type { Currency, DropTableItem } from "@loot-os/api-contract";

/** The badge on a limited entry, and the popover's heading. */
export const LIMITED_ITEM_BADGE = "Limited item";

/** True when a counted number of the entry can be won: every listing, and a fixed-stock item. */
export function isLimitedEntry(item: DropTableItem): boolean {
  return item.isLimited === true;
}

/** True when the entry is bought from an outside seller rather than held in the catalog. */
export function isMarketplaceEntry(item: DropTableItem): boolean {
  return item.itemSource === "MARKETPLACE";
}

/**
 * What the popover says for one entry. The first two lines hold for every limited entry; the
 * rest apply to an outside seller's item, which ships and cancels on different terms.
 */
export function limitedItemDisclosure(
  item: DropTableItem,
  currency: Pick<Currency, "namePlural">,
): string[] {
  const lines = [
    "A counted number of this item can be won. Another shopper can withdraw the last one first.",
    `If you cannot withdraw it, you keep it and can sell it for its value in ${currency.namePlural}.`,
  ];
  if (!isMarketplaceEntry(item)) return lines;
  return [
    ...lines,
    "Withdrawing this item buys it from an outside seller.",
    "Shipping to an address outside the US costs more, by what the seller charges above its US price. Some sellers ship to the US only.",
    "You cannot cancel a withdrawal once you have placed it. If the store cancels it for you, the item becomes a voucher worth its value.",
  ];
}

/** The supply line under an entry's name, or null when there is nothing to report. */
export function supplyMarkerText(item: DropTableItem): string | null {
  if (item.isGemSubstitute) return "Cannot be shipped; sell it for its value";
  if (!isLimitedEntry(item) || item.limitedQuantity == null) return null;
  return `${item.limitedQuantity} available`;
}

/**
 * The notes under the whole table. At most two: what a limited entry means for the box, and
 * whether any entry can no longer be shipped at all. An entry from an outside seller says more
 * than a fixed-stock catalog entry, so the first note takes the stronger form when one exists.
 */
export function dropTableDisclosureNotes(items: readonly DropTableItem[]): string[] {
  const notes: string[] = [];
  if (items.some(isMarketplaceEntry)) {
    notes.push(
      "Some items in this box come from outside sellers. Supply is limited, withdrawing to an address outside the US costs extra, and an item you cannot withdraw can be sold for its value.",
    );
  } else if (items.some(isLimitedEntry)) {
    notes.push(
      "Some items in this box have a limited supply. An item you cannot withdraw can be sold for its value.",
    );
  }
  if (items.some((item) => item.isGemSubstitute)) {
    notes.push(
      "Some items in this box can no longer be shipped. Winning one gives you the item, and you can sell it for its value.",
    );
  }
  return notes;
}
